import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Copy, Eye, EyeOff, KeyRound, RotateCcw } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Spinner } from '@/components/ui/spinner';
import { useToast } from '@/components/ui/use-toast';
import {
  useProject,
  useRotateProjectToken,
  useUpdateProject,
  useWorkspaceLanguages,
  type ProjectToken,
} from '@/lib/api';

export function ProjectSettingsTab() {
  const { wsId = '', projId = '' } = useParams<{ wsId: string; projId: string }>();
  const project = useProject(projId);
  const languages = useWorkspaceLanguages(wsId);
  const update = useUpdateProject();
  const rotate = useRotateProjectToken();
  const toast = useToast();

  const [name, setName] = useState('');
  const [sourceLanguageCode, setSource] = useState('');
  const [targets, setTargets] = useState<string[]>([]);
  const [defaultFormat, setFormat] = useState<'po' | 'pot' | 'json'>('po');
  const [revealedToken, setRevealedToken] = useState<ProjectToken | null>(null);
  const [tokenVisible, setTokenVisible] = useState(false);

  useEffect(() => {
    if (project.data) {
      setName(project.data.name);
      setSource(project.data.sourceLanguageCode);
      setTargets(project.data.targetLanguageCodes ?? []);
      setFormat((project.data.defaultFormat as 'po' | 'pot' | 'json') ?? 'po');
    }
  }, [project.data]);

  if (project.isLoading) return <Spinner label="Loading…" />;
  if (!project.data) return null;

  const targetable = (languages.data ?? []).filter((l) => l.code !== sourceLanguageCode);
  const dirty =
    name !== project.data.name ||
    sourceLanguageCode !== project.data.sourceLanguageCode ||
    !sameSet(targets, project.data.targetLanguageCodes ?? []) ||
    defaultFormat !== (project.data.defaultFormat ?? 'po');

  function toggleTarget(code: string) {
    setTargets((cur) => (cur.includes(code) ? cur.filter((c) => c !== code) : [...cur, code]));
  }

  async function onSave() {
    try {
      await update.mutateAsync({
        projectID: projId,
        name: name.trim(),
        sourceLanguageCode,
        targetLanguageCodes: targets,
        defaultFormat,
      });
      toast.show('Project saved', 'success');
    } catch (err) {
      toast.show((err as Error).message, 'error');
    }
  }

  async function onRotate() {
    if (
      !window.confirm(
        'Rotate the CLI token? The current token will stop working immediately and the new one is shown once.',
      )
    )
      return;
    try {
      const result = await rotate.mutateAsync(projId);
      setRevealedToken(result);
      setTokenVisible(true);
      toast.show('Token rotated — copy it now', 'warning');
    } catch (err) {
      toast.show((err as Error).message, 'error');
    }
  }

  async function copyToken() {
    if (!revealedToken?.token) return;
    try {
      await navigator.clipboard.writeText(revealedToken.token);
      toast.show('Token copied to clipboard', 'success');
    } catch {
      toast.show('Copy failed — select the text manually', 'error');
    }
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>General</CardTitle>
          <CardDescription>
            Name, source language, and the default file format for round-tripping.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="settings-name">Name</Label>
              <Input
                id="settings-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="settings-slug">Slug</Label>
              <Input
                id="settings-slug"
                value={project.data.slug}
                disabled
                readOnly
                aria-describedby="slug-hint"
              />
              <p id="slug-hint" className="text-[11px] text-muted-foreground">
                Slug is immutable — used in CLI commands and webhooks.
              </p>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="settings-src">Source language</Label>
              <select
                id="settings-src"
                value={sourceLanguageCode}
                onChange={(e) => setSource(e.target.value)}
                className="flex h-9 w-full rounded-md border border-input bg-surface px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                {(languages.data ?? []).map((lang) => (
                  <option key={lang.id} value={lang.code}>
                    {lang.code} {lang.label ? `— ${lang.label}` : ''}
                  </option>
                ))}
              </select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="settings-fmt">Default format</Label>
              <select
                id="settings-fmt"
                value={defaultFormat}
                onChange={(e) => setFormat(e.target.value as 'po' | 'pot' | 'json')}
                className="flex h-9 w-full rounded-md border border-input bg-surface px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <option value="po">PO (gettext)</option>
                <option value="pot">POT (template)</option>
                <option value="json">JSON</option>
              </select>
            </div>
          </div>

          <div className="space-y-2">
            <Label>Target languages</Label>
            <div className="flex flex-wrap gap-2">
              {targetable.map((lang) => {
                const active = targets.includes(lang.code);
                return (
                  <button
                    key={lang.id}
                    type="button"
                    aria-pressed={active}
                    onClick={() => toggleTarget(lang.code)}
                    className={
                      active
                        ? 'inline-flex items-center gap-1.5 rounded-md border border-primary bg-primary/10 px-2.5 py-1 text-xs font-medium text-primary'
                        : 'inline-flex items-center gap-1.5 rounded-md border border-border bg-surface px-2.5 py-1 text-xs font-medium text-foreground hover:border-primary/40'
                    }
                  >
                    {lang.code}
                    {lang.label && <span className="text-muted-foreground">{lang.label}</span>}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="flex items-center justify-end gap-2 border-t border-border pt-4">
            <Button
              variant="outline"
              onClick={() => {
                if (project.data) {
                  setName(project.data.name);
                  setSource(project.data.sourceLanguageCode);
                  setTargets(project.data.targetLanguageCodes ?? []);
                  setFormat((project.data.defaultFormat as 'po' | 'pot' | 'json') ?? 'po');
                }
              }}
              disabled={!dirty || update.isPending}
            >
              Reset
            </Button>
            <Button onClick={() => void onSave()} disabled={!dirty || update.isPending}>
              {update.isPending ? 'Saving…' : 'Save changes'}
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="inline-flex items-center gap-2">
            <KeyRound className="size-4 text-primary" aria-hidden />
            CLI token
          </CardTitle>
          <CardDescription>
            The CLI authenticates with an <code>fxp_…</code> token. Rotation invalidates the
            previous token immediately.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <span className="text-muted-foreground">Current prefix:</span>
            <Badge tone="outline">{project.data.tokenPrefix ?? '—'}</Badge>
          </div>
          {revealedToken && (
            <div className="space-y-2 rounded-md border border-warning/40 bg-warning/10 p-3">
              <p className="text-xs font-medium text-warning">
                Stored only on this page load. Copy it now.
              </p>
              <div className="flex items-center gap-2">
                <Input
                  readOnly
                  value={revealedToken.token}
                  type={tokenVisible ? 'text' : 'password'}
                  className="font-mono"
                  onFocus={(e) => e.target.select()}
                />
                <Button
                  size="icon"
                  variant="outline"
                  aria-label={tokenVisible ? 'Hide token' : 'Reveal token'}
                  onClick={() => setTokenVisible((v) => !v)}
                >
                  {tokenVisible ? <EyeOff aria-hidden /> : <Eye aria-hidden />}
                </Button>
                <Button size="icon" variant="outline" onClick={() => void copyToken()} aria-label="Copy token">
                  <Copy aria-hidden />
                </Button>
              </div>
            </div>
          )}
          <div className="flex items-center justify-end">
            <Button variant="outline" onClick={() => void onRotate()} disabled={rotate.isPending}>
              <RotateCcw aria-hidden />
              {rotate.isPending ? 'Rotating…' : 'Rotate token'}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function sameSet(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false;
  const set = new Set(a);
  return b.every((v) => set.has(v));
}
